import React from 'react';
import classnames from 'classnames';
import { NavLink } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CollaboratorAvatar, Modal, TokenIntegrationModalContent, Toast } from 'components';
import Icon from 'assets/icons';

import {
  VerticalIntegrationIcons,
  VerticalListItem,
  VerticalListToggle,
  SkeletonVerticalNavigation,
  GroupList,
} from './components';

import gql from 'graphql-tag';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import { useSidebarState, useSidebarDispatch, useFeatureFlagState } from 'context';
import { useTranslation } from 'hooks';

import {
  Logger,
  trackingVerticalNavigationselectActivityFeedClick,
  trackingVerticalNavigationUserProfileClick,
  trackingVerticalNavigationAppClick,
} from 'services';
import { openAuthURL, maxIntegrationsWithGroups, maxIntegrationsNoGroups } from 'helpers';
import {
  getIntegrationToken,
  generateIntegrationOAuthUrl,
  groupIntegrations,
} from 'helpers/integrations';

import styles from './VerticalNavigation.module.scss';

export const GetVerticalNavigationQuery = gql`
  query getVerticalNavigation {
    me {
      userProfile {
        id
        name
        email
        avatarUrl
      }
      organization {
        id
      }
      integrations {
        id
        title
        type
        broken
        integrationUserId
        features {
          configurable
        }
      }
    }
    integrations {
      type
      title
      setupPath
      userConfig {
        instructions
        fields {
          name
          label
        }
      }
      features {
        configurable
      }
    }
  }
`;

interface VerticalNavigationData {
  me: {
    userProfile: User;
    organization: Organization;
    integrations: UserIntegration[];
  };
  integrations: Integration[];
}

export const VerticalNavigation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const client = useApolloClient();
  const sidebarState = useSidebarState();
  const sidebarDispatch = useSidebarDispatch();
  const { groupsFeatureFlag } = useFeatureFlagState();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [showAllIntegrations, setShowAllIntegrations] = React.useState(false);
  const [integrationToConfigure, setIntegrationToConfigure] = React.useState<Integration>();
  const isGhosted = location.pathname.includes('manage-apps');
  const openDrawer = sidebarState.open;

  const { error, data } = useQuery<VerticalNavigationData>(GetVerticalNavigationQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const userIntegrations = data?.me.integrations || [];
  const groupedIntegrations = React.useMemo(() => groupIntegrations(userIntegrations), [
    userIntegrations,
  ]);

  const maxIntegrations = groupsFeatureFlag ? maxIntegrationsWithGroups : maxIntegrationsNoGroups;

  const integrationsToShow = showAllIntegrations
    ? groupedIntegrations
    : groupedIntegrations.slice(0, maxIntegrations);

  const closeSidebar = React.useCallback(() => {
    sidebarDispatch({ type: 'CLOSE_SIDEBAR' });
  }, [sidebarDispatch]);

  if (data === undefined) return <SkeletonVerticalNavigation />;

  if (error) return <SkeletonVerticalNavigation />;

  const connectIntegration = (userIntegration: UserIntegration) => {
    const id = userIntegration.type!.slice(0, userIntegration.type!.indexOf('@'));
    const integration = data.integrations.find(
      (integration) => userIntegration.type === integration.type
    );

    if (integration!.userConfig) {
      setIntegrationToConfigure(integration);
      setModalOpen(true);
    } else {
      getIntegrationToken().then((token) => {
        const url = generateIntegrationOAuthUrl(
          token,
          integration!.setupPath!,
          id,
          data.me.userProfile.id!,
          data.me.organization.id!
        );
        openAuthURL(
          url,
          true,
          '_blank',
          () => {
            client
              .query({
                query: GetVerticalNavigationQuery,
                fetchPolicy: 'network-only',
              })
              .catch((error) => {
                Logger.error(error);
              });
          },
          () => {
            toast(<Toast message={t('toasts.pop-up-blocked')} hasCloseButton />, {
              position: toast.POSITION.BOTTOM_LEFT,
              hideProgressBar: true,
              closeButton: false,
              closeOnClick: false,
              autoClose: false,
            });
          }
        );
      });
    }
  };

  return (
    <>
      <div
        className={classnames(styles.appsColumn, styles.container, openDrawer && styles.openDrawer)}
      >
        <div className={classnames(styles.innerWrap, isGhosted && styles.ghosted)}>
          <span className={styles.appsClose} onClick={closeSidebar}>
            <Icon.MenuClose />
          </span>

          {/* User Profile */}
          <VerticalListItem
            type="profile"
            user={data.me.userProfile}
            name={data.me.userProfile.name || data.me.userProfile.email || ''}
            icon={
              <CollaboratorAvatar
                bordered={false}
                className={styles.avatar}
                inheritSize
                size="Small"
                collaborator={{
                  displayName: data.me.userProfile.name || '',
                  emailAddress: data.me.userProfile.email || '',
                  photoURL: data.me.userProfile.avatarUrl || '',
                }}
              />
            }
            trackingEvent={trackingVerticalNavigationUserProfileClick}
          />

          {/* Link to Activity Feed */}
          <VerticalListItem
            type="activity-feed"
            name="Activity Feed"
            icon={<Icon.ActivityFeed className={styles.icon} textPosition="Left" />}
            trackingEvent={trackingVerticalNavigationselectActivityFeedClick}
          />

          {/* Groups Section */}
          {groupsFeatureFlag && (
            <>
              <h1 className={styles.heading}>Groups</h1>
              <GroupList className={styles.groupList} />
            </>
          )}

          {/* List of top Integrations */}
          <h1 className={styles.heading}>Apps</h1>
          {integrationsToShow.map((integrations: UserIntegration[], key: number) => (
            <VerticalListItem
              key={key}
              type="integration"
              name={integrations[0].title}
              user={data.me.userProfile}
              integrations={integrations}
              connectIntegration={connectIntegration}
              icon={
                <VerticalIntegrationIcons
                  className={styles.avatar}
                  itemIntegrationName={integrations[0].type!.slice(
                    0,
                    integrations[0].type!.indexOf('@')
                  )}
                />
              }
              trackingEvent={() => {
                trackingVerticalNavigationAppClick(
                  integrations[0].type!.slice(0, integrations[0].type!.indexOf('@'))
                );
              }}
            />
          ))}

          {/* Add Apps Button */}
          <NavLink className={styles.moreAppsButton} to={`/manage-apps`}>
            <Icon.PlusCircle className={styles.icon} />
            Add App
          </NavLink>

          {/* Show more/less integrations */}
          {!showAllIntegrations && groupedIntegrations.length > maxIntegrations && (
            <VerticalListToggle
              showAllIntegrations={showAllIntegrations}
              setShowAllIntegrations={setShowAllIntegrations}
            />
          )}
        </div>
        {isGhosted && <NavLink className={styles.invisiBackButton} to={`/`} />}
      </div>

      <div
        className={classnames(styles.overlay, openDrawer && styles.open)}
        onClick={closeSidebar}
      />

      <Modal isOpen={modalOpen} onRequestClose={() => setModalOpen(false)}>
        <TokenIntegrationModalContent
          integration={integrationToConfigure}
          closeModal={() => setModalOpen(false)}
        />
      </Modal>
    </>
  );
};
